import { RecoveryScope } from './connectionRecoveryCoordinator';

export interface MasterPlanVersionObservation {
    masterPlanVersion: number;
}

export interface StowageRecoveryDependencies {
    getActiveMasterPlanVersion: () => Promise<MasterPlanVersionObservation | undefined>;
    reloadMasterGraph: () => Promise<boolean>;
    isCurrent: () => boolean;
}

export interface StowageRecoveryResult {
    recovered: boolean;
    reloaded: boolean;
}

/**
 * Keeps active MasterPlanVersion observations scoped to the authenticated
 * vessel session. Supported UI edits are copy-on-write to Draft; only a
 * promoted Master changes this token. A changed token is reloaded once, then
 * checked again so a concurrent Master promotion cannot leave the graph stale.
 */
export class StowageMasterPlanVersionRecovery {
    private readonly masterPlanVersions = new Map<string, number>();

    public async recover(scope: RecoveryScope, dependencies: StowageRecoveryDependencies): Promise<StowageRecoveryResult> {
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: false };
        }

        const key = this.getKey(scope);
        const observed = await dependencies.getActiveMasterPlanVersion();
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: false };
        }
        const observedMasterPlanVersion = observed?.masterPlanVersion;
        if (!this.isValidMasterPlanVersion(observedMasterPlanVersion)) {
            return { recovered: false, reloaded: false };
        }

        const knownMasterPlanVersion = this.masterPlanVersions.get(key);
        if (knownMasterPlanVersion === undefined) {
            if (!dependencies.isCurrent()) {
                return { recovered: false, reloaded: false };
            }
            this.masterPlanVersions.set(key, observedMasterPlanVersion);
            return { recovered: true, reloaded: false };
        }

        if (knownMasterPlanVersion === observedMasterPlanVersion) {
            return { recovered: true, reloaded: false };
        }

        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: false };
        }
        const firstReloaded = await dependencies.reloadMasterGraph();
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        if (!firstReloaded) {
            return { recovered: false, reloaded: true };
        }

        const postLoadMasterPlanVersion = await dependencies.getActiveMasterPlanVersion();
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        if (!this.isValidMasterPlanVersion(postLoadMasterPlanVersion?.masterPlanVersion)) {
            return { recovered: false, reloaded: true };
        }
        const postLoadVersion = postLoadMasterPlanVersion.masterPlanVersion;

        if (postLoadVersion === observedMasterPlanVersion) {
            if (!dependencies.isCurrent()) {
                return { recovered: false, reloaded: true };
            }
            this.masterPlanVersions.set(key, postLoadVersion);
            return { recovered: true, reloaded: true };
        }

        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        const secondReloaded = await dependencies.reloadMasterGraph();
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        if (!secondReloaded) {
            return { recovered: false, reloaded: true };
        }

        const finalMasterPlanVersion = await dependencies.getActiveMasterPlanVersion();
        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        if (!this.isValidMasterPlanVersion(finalMasterPlanVersion?.masterPlanVersion)) {
            return { recovered: false, reloaded: true };
        }
        const finalVersion = finalMasterPlanVersion.masterPlanVersion;
        if (finalVersion !== postLoadVersion) {
            // A second mutation raced the bounded retry. Do not claim that the
            // graph is current; the next reconnect/manual recovery can retry.
            return { recovered: false, reloaded: true };
        }

        if (!dependencies.isCurrent()) {
            return { recovered: false, reloaded: true };
        }
        this.masterPlanVersions.set(key, finalVersion);
        return { recovered: true, reloaded: true };
    }

    public observe(scope: RecoveryScope, observation: MasterPlanVersionObservation): boolean {
        if (!this.isValidMasterPlanVersion(observation.masterPlanVersion)) {
            return false;
        }

        this.masterPlanVersions.set(this.getKey(scope), observation.masterPlanVersion);
        return true;
    }

    public invalidateSession(session: number): void {
        const prefix = `${session}:`;
        Array.from(this.masterPlanVersions.keys())
            .filter((key) => !key.startsWith(prefix))
            .forEach((key) => this.masterPlanVersions.delete(key));
    }

    private isValidMasterPlanVersion(masterPlanVersion: number | undefined): masterPlanVersion is number {
        return Number.isSafeInteger(masterPlanVersion) && masterPlanVersion > 0;
    }

    private getKey(scope: RecoveryScope): string {
        return `${scope.session}:${scope.vessel}`;
    }
}
